import React, { useState } from 'react';
import moment from 'moment';
import { skipToken } from '@reduxjs/toolkit/query';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Spinner, Form } from 'react-bootstrap';

import {
  useGetHRXAttendanceByIdQuery,
  useUpdateWorkSessionMutation,
  useUpdateBreakSessionMutation
} from '../../../../redux/apis/attendanceApi';
import { useIsLoggedInQuery } from '../../../../redux/apis/authApi';
import { setSessionShow } from '../../../../redux/Slice/hrxAttendanceSlice';
import { selectDisplayTimezone } from '../../../../redux/utils/apiLocality';

import { formatterTime } from '../../../../utils/handler';
import { ROLES } from '../../../../utils/constant/global';
import {
  datetimeLocalInTimezoneToUtcMs,
  formatTimestampInTimezone,
  toDatetimeLocalInTimezone
} from '../../../../utils/timedoctorAttendanceHelpers';

const SessionDialog = () => {
  const dispatch = useDispatch();
  const { show, selectedSession } = useSelector((state) => state.hrxAttendance);
  const timeZone = useSelector(selectDisplayTimezone) || 'Asia/Kolkata';

  const { data: isLoggedInData } = useIsLoggedInQuery();

  const { data, isLoading, isFetching, refetch } = useGetHRXAttendanceByIdQuery(
    selectedSession ? { id: selectedSession } : skipToken
  );

  const attendanceData = data?.data?.length > 0 ? data.data[0] : {};

  const [expandedRows, setExpandedRows] = useState({});
  const [editModal, setEditModal] = useState(false);
  const [editType, setEditType] = useState(null); // 'work' or 'break'
  const [selectedSessionData, setSelectedSessionData] = useState(null);
  const [updateWorkSession] = useUpdateWorkSessionMutation();
  const [updateBreakSession] = useUpdateBreakSessionMutation();

  const toggleRow = (index) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleEdit = (type, sessionData) => {
    setEditType(type);
    setSelectedSessionData({
      ...sessionData,
      checkIn: sessionData.checkInTime
        ? toDatetimeLocalInTimezone(sessionData.checkInTime, timeZone)
        : '',
      checkOut: sessionData.checkOutTime
        ? toDatetimeLocalInTimezone(sessionData.checkOutTime, timeZone)
        : '',
      breakIn: sessionData.breakIn ? toDatetimeLocalInTimezone(sessionData.breakIn, timeZone) : '',
      breakOut: sessionData.breakOut
        ? toDatetimeLocalInTimezone(sessionData.breakOut, timeZone)
        : ''
    });
    setEditModal(true);
  };

  const handleSave = async () => {
    if (!selectedSessionData) return;

    if (editType === 'work') {
      await updateWorkSession({
        id: selectedSessionData._id,
        data: {
          checkInTime: datetimeLocalInTimezoneToUtcMs(selectedSessionData.checkIn, timeZone),
          checkOutTime: datetimeLocalInTimezoneToUtcMs(selectedSessionData.checkOut, timeZone)
        }
      });
    } else if (editType === 'break') {
      await updateBreakSession({
        id: selectedSessionData._id,
        data: {
          breakIn: datetimeLocalInTimezoneToUtcMs(selectedSessionData.breakIn, timeZone),
          breakOut: datetimeLocalInTimezoneToUtcMs(selectedSessionData.breakOut, timeZone)
        }
      });
    }

    setEditModal(false);
    refetch(); // Refresh data after update
  };

  return (
    <>
      <Modal
        show={show}
        onHide={() => {
          dispatch(setSessionShow(false));
          setExpandedRows({});
        }}
        size="lg"
        centered
        dialogClassName="hrx-session-modal"
        contentClassName="hrx-session-modal__content"
      >
        <div className="hrx-session-modal__header">
          <div className="hrx-session-modal__header-content">
            <span className="hrx-session-modal__icon" aria-hidden>
              <i className="icofont-clock-time" />
            </span>
            <div className="hrx-session-modal__header-copy">
              <h5 className="hrx-session-modal__title">Work Session & Break Summary</h5>
              {data?.data?.date ? (
                <p className="hrx-session-modal__subtitle">
                  {moment(data.data.date, 'YYYYMMDD').format('DD MMM YYYY')}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="btn-close hrx-session-modal__close"
            aria-label="Close"
            onClick={() => {
              dispatch(setSessionShow(false));
              setExpandedRows({});
            }}
          />
        </div>
        <div className="hrx-session-modal__body">
          <div className="hrx-session-modal__table-wrap">
            <table className="hrx-session-modal__table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Check-In</th>
                  <th>Check-Out</th>
                  <th>Total Worked</th>
                  <th>Break Time</th>
                  <th>Break Sessions</th>
                  {isLoggedInData?.data?.hrxRole?.roleName === ROLES.ADMIN ||
                    (isLoggedInData?.data?.hrxRole?.roleName === ROLES.HR && <th>Actions</th>)}
                </tr>
              </thead>
              <tbody>
                {isLoading || isFetching ? (
                  <tr>
                    <td colSpan="7">
                      <div className="hrx-session-modal__state">
                        <Spinner animation="border" size="sm" />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {attendanceData?.sessions?.length > 0 ? (
                      attendanceData.sessions.map((session, index) => {
                        return (
                          <React.Fragment key={index}>
                            <tr>
                              <td className="hrx-session-modal__index">{index + 1}</td>
                              <td className="hrx-session-modal__in">
                                {session.checkInTime
                                  ? formatTimestampInTimezone(session.checkInTime, timeZone)
                                  : '-'}
                              </td>
                              <td
                                className={
                                  session.checkOutTime
                                    ? 'hrx-session-modal__out'
                                    : 'hrx-session-modal__ongoing'
                                }
                              >
                                {session.checkOutTime
                                  ? formatTimestampInTimezone(session.checkOutTime, timeZone)
                                  : 'Ongoing'}
                              </td>
                              <td>
                                {session?.totalWorkedMilliseconds
                                  ? formatterTime(session.totalWorkedMilliseconds / 1000)
                                  : '-'}
                              </td>
                              <td>
                                {session?.totalBreakMilliseconds
                                  ? formatterTime(session.totalBreakMilliseconds / 1000)
                                  : '—'}
                              </td>
                              <td>
                                {session.breakSessions?.length ? (
                                  <button
                                    type="button"
                                    className={`hrx-session-modal__view${expandedRows[index] ? ' is-open' : ''}`}
                                    onClick={() => toggleRow(index)}
                                  >
                                    {expandedRows[index] ? 'Hide' : 'View'}
                                  </button>
                                ) : (
                                  <span className="hrx-session-modal__empty">No breaks</span>
                                )}
                              </td>
                              {isLoggedInData?.data?.hrxRole?.roleName === ROLES.ADMIN ||
                                (isLoggedInData?.data?.hrxRole?.roleName === ROLES.HR && (
                                  <td>
                                    <button
                                      type="button"
                                      className="hrx-session-modal__edit"
                                      aria-label="Edit work session"
                                      onClick={() => handleEdit('work', session)}
                                    >
                                      <i className="icofont-edit" aria-hidden />
                                    </button>
                                  </td>
                                ))}
                            </tr>

                            {expandedRows[index] && session.breakSessions?.length > 0 && (
                              <tr className="hrx-session-modal__break-row">
                                <td colSpan="7">
                                  <div className="hrx-session-modal__breaks">
                                    <p className="hrx-session-modal__breaks-title">
                                      Break sessions
                                    </p>
                                    <table className="hrx-session-modal__table hrx-session-modal__table--nested">
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Break-In</th>
                                          <th>Break-Out</th>
                                          <th>Duration</th>
                                          {isLoggedInData?.data?.hrxRole?.roleName ===
                                            ROLES.ADMIN ||
                                            (isLoggedInData?.data?.hrxRole?.roleName ===
                                              ROLES.HR && <th>Actions</th>)}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {session.breakSessions.map((breakItem, bIndex) => {
                                          const breakDuration =
                                            Number(breakItem.breakOut) - Number(breakItem.breakIn);
                                          return (
                                            <tr key={bIndex}>
                                              <td className="hrx-session-modal__index">
                                                {bIndex + 1}
                                              </td>
                                              <td>
                                                {breakItem.breakIn
                                                  ? formatTimestampInTimezone(
                                                      breakItem.breakIn,
                                                      timeZone
                                                    )
                                                  : '-'}
                                              </td>
                                              <td>
                                                {breakItem.breakOut
                                                  ? formatTimestampInTimezone(
                                                      breakItem.breakOut,
                                                      timeZone
                                                    )
                                                  : '-'}
                                              </td>
                                              <td className="hrx-session-modal__duration">
                                                {formatterTime(breakDuration / 1000)}
                                              </td>
                                              {isLoggedInData?.data?.hrxRole?.roleName ===
                                                ROLES.ADMIN ||
                                                (isLoggedInData?.data?.hrxRole?.roleName ===
                                                  ROLES.HR && (
                                                  <td>
                                                    <button
                                                      type="button"
                                                      className="hrx-session-modal__edit"
                                                      aria-label="Edit break session"
                                                      onClick={() => handleEdit('break', breakItem)}
                                                    >
                                                      <i className="icofont-edit" aria-hidden />
                                                    </button>
                                                  </td>
                                                ))}
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="hrx-session-modal__state">
                          No session data available
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal show={editModal} onHide={() => setEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit {editType === 'work' ? 'Work Session' : 'Break Session'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center gap-2">
            <Form.Group>
              <Form.Label>{editType === 'work' ? 'Check-In' : 'Break-In'}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={
                  editType === 'work' ? selectedSessionData?.checkIn : selectedSessionData?.breakIn
                }
                onChange={(e) =>
                  setSelectedSessionData((prev) => ({
                    ...prev,
                    [editType === 'work' ? 'checkIn' : 'breakIn']: e.target.value
                  }))
                }
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{editType === 'work' ? 'Check-Out' : 'Break-Out'}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={
                  editType === 'work'
                    ? selectedSessionData?.checkOut
                    : selectedSessionData?.breakOut
                }
                onChange={(e) =>
                  setSelectedSessionData((prev) => ({
                    ...prev,
                    [editType === 'work' ? 'checkOut' : 'breakOut']: e.target.value
                  }))
                }
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-success" onClick={handleSave}>
            Save
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SessionDialog;
